import { useState } from 'react';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRegionalSettings } from '@/hooks/useRegionalSettings';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from 'recharts';

interface PricePoint {
  date: string;
  price: number;
  source?: 'app' | 'external';
}

interface PriceHistoryChartProps {
  data: PricePoint[];
  currentPrice: number;
  currency?: string;
  className?: string;
  externalSource?: string | null;
  hasExternalData?: boolean;
}

type TimeRange = '1W' | '1M' | '3M' | '6M' | 'ALL';

const filterDataByRange = (data: PricePoint[], range: TimeRange): PricePoint[] => {
  if (range === 'ALL') return data;
  
  const now = new Date();
  const daysMap: Record<TimeRange, number> = {
    '1W': 7,
    '1M': 30,
    '3M': 90,
    '6M': 180,
    'ALL': Infinity,
  };
  
  const cutoffDate = new Date(now.getTime() - daysMap[range] * 24 * 60 * 60 * 1000);
  return data.filter(point => new Date(point.date) >= cutoffDate);
};

export function PriceHistoryChart({ 
  data, 
  currentPrice, 
  currency = 'USD',
  className,
  externalSource,
  hasExternalData = false,
}: PriceHistoryChartProps) {
  const [selectedRange, setSelectedRange] = useState<TimeRange>('3M');
  const { formatPrice } = useRegionalSettings();
  
  const filteredData = filterDataByRange(data, selectedRange);
  
  // Calculate stats
  const prices = filteredData.map(d => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
  const firstPrice = prices[0] || currentPrice;
  const priceChange = ((currentPrice - firstPrice) / firstPrice) * 100;
  
  // Determine trend
  const isDown = priceChange < -1;
  const isUp = priceChange > 1;
  
  const ranges: TimeRange[] = ['1W', '1M', '3M', '6M', 'ALL'];
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-surface-3 border border-white/10 rounded-xl px-3 py-2 shadow-lg">
          <p className="text-xs text-muted-foreground mb-1">
            {new Date(label).toLocaleDateString('en-GB', { 
              day: 'numeric', 
              month: 'short',
              year: '2-digit'
            })}
          </p>
          <p className="text-sm font-semibold text-foreground">
            {formatPrice(payload[0].value, currency)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground">Price History</h3>
          {hasExternalData && (
            <span className="text-[10px] text-muted-foreground bg-surface-2 px-1.5 py-0.5 rounded">
              Historical data
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isDown && (
            <span className="flex items-center gap-1 text-priceDrop text-sm font-medium">
              <TrendingDown className="w-4 h-4" />
              {Math.abs(priceChange).toFixed(0)}%
            </span>
          )}
          {isUp && (
            <span className="flex items-center gap-1 text-destructive text-sm font-medium">
              <TrendingUp className="w-4 h-4" />
              {priceChange.toFixed(0)}%
            </span>
          )}
          {!isDown && !isUp && (
            <span className="flex items-center gap-1 text-muted-foreground text-sm font-medium">
              <Minus className="w-4 h-4" />
              Stable
            </span>
          )}
        </div>
      </div>
      
      {/* Chart */}
      <div className="h-40 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart 
            data={filteredData}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop 
                  offset="0%" 
                  stopColor={isDown ? 'hsl(var(--price-drop))' : isUp ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'} 
                  stopOpacity={0.3} 
                />
                <stop 
                  offset="100%" 
                  stopColor={isDown ? 'hsl(var(--price-drop))' : isUp ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'} 
                  stopOpacity={0} 
                />
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="date" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              }}
              interval="preserveStartEnd"
              minTickGap={50}
            />
            <YAxis 
              domain={['auto', 'auto']}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value) => formatPrice(value, currency) || ''}
              width={55}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine 
              y={avgPrice} 
              stroke="hsl(var(--muted-foreground))" 
              strokeDasharray="3 3" 
              strokeOpacity={0.3}
            />
            <Area
              type="monotone"
              dataKey="price"
              stroke={isDown ? 'hsl(var(--price-drop))' : isUp ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'}
              strokeWidth={2}
              fill="url(#priceGradient)"
              animationDuration={750}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {/* Time Range Selector */}
      <div className="flex items-center justify-center gap-1 bg-surface-2 rounded-full p-1">
        {ranges.map((range) => (
          <button
            key={range}
            onClick={() => setSelectedRange(range)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-200',
              selectedRange === range
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {range}
          </button>
        ))}
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-surface-2 rounded-xl p-3 text-center">
          <p className="text-xs text-muted-foreground mb-1">Lowest</p>
          <p className="font-semibold text-priceDrop">
            {formatPrice(minPrice, currency)}
          </p>
        </div>
        <div className="bg-surface-2 rounded-xl p-3 text-center">
          <p className="text-xs text-muted-foreground mb-1">Average</p>
          <p className="font-semibold text-foreground">
            {formatPrice(avgPrice, currency)}
          </p>
        </div>
        <div className="bg-surface-2 rounded-xl p-3 text-center">
          <p className="text-xs text-muted-foreground mb-1">Highest</p>
          <p className="font-semibold text-destructive">
            {formatPrice(maxPrice, currency)}
          </p>
        </div>
      </div>
    </div>
  );
}

// Generate mock price history data
export function generateMockPriceHistory(
  currentPrice: number, 
  months: number = 6
): PricePoint[] {
  const data: PricePoint[] = [];
  const now = new Date();
  const variance = currentPrice * 0.25; // 25% variance
  
  for (let i = months * 30; i >= 0; i -= 3) {
    const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
    // Create realistic price fluctuation
    const randomFactor = Math.sin(i / 15) * 0.5 + Math.random() * 0.5;
    const price = currentPrice + (variance * randomFactor) - (variance * 0.3);
    
    data.push({
      date: date.toISOString().split('T')[0],
      price: Math.max(price, currentPrice * 0.6), // Never below 60% of current
    });
  }
  
  // Ensure last point is current price
  data[data.length - 1].price = currentPrice;
  
  return data;
}